























































import {
	computed,
	defineComponent,
	Ref,
	ref,
} from '@vue/composition-api';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import ErrorCard from '@front-app/components/cards/state/ErrorCard.vue';
import { Size, Spacing } from '@front-app/components/constants';
import TrForm from '@front-app/components/form/Form.vue';
import ColumnHeader from '@front-app/components/layouts/columns/ColumnHeader.vue';
import { Routes } from '@front-app/constants/routes';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import AddInvitees from '@front-app/modules/group/components/admin/addMembers/AddInvitees.vue';
import NotificationSelect from '@front-app/modules/group/components/admin/addMembers/NotificationSelect.vue';
import CustomizeEmailText from '@front-app/modules/group/components/admin/addMembers/CustomizeEmailText.vue';
import {
	INVITE_STEP,
	INVITE_BY,
	MembersToAdd,
	MemberTypeOption,
} from '@front-app/modules/group/components/admin/addMembers/addMembers';
import { useAdminAccess } from '@front-app/modules/group/hooks/useAdminAccess';
import { inviteCampaignEmailStorage } from '@front-app/modules/group/hooks/useInviteCampaignEmailStorage';
import { GroupWithCommunity } from '@front-app/services/GroupsApi/GroupModel';
import { inviteGroupMembers } from '@front-app/services/GroupsApi/GroupsMemberApi';
import {
	idsToColleagueInvites,
	emailsToExternalInvites,
} from '@front-app/modules/group/util/invite';
import { UserRole } from '@front-app/services/UserApi/UserModel';
import { GroupsStore } from '@front-app/store/singletons/Groups';

export default defineComponent({
	components: {
		AddInvitees,
		ColumnHeader,
		CustomizeEmailText,
		ErrorCard,
		NotificationSelect,
		TrForm,
	},
	props: {
		groupId: {
			type: String,
			required: true,
		},
	},
	setup(props, ctx) {
		const group = computed(() => GroupsStore.getInstance().groupById(props.groupId));
		const { hasAddMembersAccess } = useAdminAccess(group);

		const emailAddressList: Ref<string[]> = ref([]);
		const selectedColleagueIds: Ref<string[]> = ref([]);
		const emailsList = ref('');
		const selectedRole: Ref<MemberTypeOption> = ref(UserRole.MEMBER);

		// Steps
		const currentStep = ref(INVITE_STEP.ADD_INVITEES);
		const goToNextStep = (nextStep: INVITE_STEP) => {
			currentStep.value = nextStep;
		};
		const addInvitees = (invitees: MembersToAdd) => {
			if (invitees.type === INVITE_BY.EMAIL_ADDRESS) {
				emailAddressList.value = invitees.members;
			} else {
				selectedColleagueIds.value = invitees.members;
			}
			selectedRole.value = invitees.role;
			emailsList.value = invitees.emailsList;
			goToNextStep(INVITE_STEP.NOTIFICATION_SELECT);
		};
		// END Steps

		// Sending invites
		const {
			track: trackInvitationStatus,
			status: invitationSendStatus,
		} = useAsyncStatus();

		const sendInvites = async (emailText: string) => {
			const colleagueInvites = idsToColleagueInvites(selectedColleagueIds.value, selectedRole.value);
			const externalInvites = emailsToExternalInvites(emailAddressList.value, selectedRole.value);

			await trackInvitationStatus(inviteGroupMembers(props.groupId, {
				subject: getEmailSubject(group.value), // The API only supports this for external invitations
				message: emailText,
				// Send emails and direct messages to colleagues
				inviteInternal: true,
				// Send emails and direct messages to users invited by email who already have a TimedRight account
				inviteExternal: true,
				// Send emails to users invited by email who don't have a TimedRight account
				emailExternal: true,
				colleagues: colleagueInvites,
				external: externalInvites,
			}));
		};
		// END Sending invites

		// Notification step
		const numUsersAdded = computed(() => emailAddressList.value.length || selectedColleagueIds.value.length);
		// END Notification step

		// Invite campaign
		const routeToInviteCampaign = () => {
			inviteCampaignEmailStorage.set(emailsList.value);

			ctx.root.$router.push({
				name: Routes.GROUP_ADMIN_INVITE_CAMPAIGN,
				params: { groupId: props.groupId },
			});
		};
		// END Invite campaign

		return {
			group,
			hasAddMembersAccess,
			GROUP_MAIN_ROUTE: Routes.GROUP_MAIN,
			// Invite steps
			INVITE_STEP,
			currentStep,
			goToNextStep,
			addInvitees,
			// Sending invites
			sendInvites,
			invitationSendStatus,
			// Notification step
			selectedRole,
			numUsersAdded,
			routeToInviteCampaign,
			// Util
			ButtonPalettes,
			Size,
			Spacing,
		};
	},
});

function getEmailSubject(group: GroupWithCommunity | undefined): string {
	return group ? `Invite to the ${group.name} group on TimedRight` : 'Invite to join a group on TimedRight';
}
